import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { add } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaUserPlus, 
  FaEnvelope, 
  FaUser, 
  FaGlobe, 
  FaSave, 
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap'
import './AddUser.css'

// eslint-disable-next-line
export default function (props) {
  const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()

  const [userEmail, setEmail] = useState('')
  const [validEmail, setValidEmail] = useState(true)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [country, setCountry] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  
  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
    if(e.target.value === '') {
      setValidEmail(true)
      setErrors(prev => ({ ...prev, email: '' }))
    } else {
      const isValid = EMAIL_REGEX.test(e.target.value)
      setValidEmail(isValid)
      setErrors(prev => ({
        ...prev,
        email: isValid ? '' : 'Correo con formato inválido'
      }))
    }
  }

  const handleChangeFirstName = (e) => {
    setFirstName(e.target.value)
    setErrors(prev => ({
      ...prev,
      firstName: e.target.value.trim() === '' ? 'El nombre es requerido' : ''
    }))
  }

  const handleChangeLastName = (e) => {
    setLastName(e.target.value)
    setErrors(prev => ({
      ...prev,
      lastName: e.target.value.trim() === '' ? 'Los apellidos son requeridos' : ''
    }))
  }

  const handleChangeCountry = (e) => {
    setCountry(e.target.value)
    setErrors(prev => ({
      ...prev,
      country: e.target.value.trim() === '' ? 'El país es requerido' : ''
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!userEmail.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!validEmail) {
      newErrors.email = 'Correo con formato inválido'
    }

    if (!firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido'
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Los apellidos son requeridos'
    }

    if (!country.trim()) {
      newErrors.country = 'El país es requerido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      setAlert({
        message: 'Por favor corrige los errores en el formulario',
        msgType: 'warning'
      })
      return
    }

    setIsLoading(true)

    try {
      let userData = await add({ 
        path: 'users',
        data: {
          userEmail: userEmail.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          country: country.trim(),
          roles: ['ADMIN'],
        }, 
        token: authenticatedUser.value 
      })

      if(Object.prototype.hasOwnProperty.call(userData, 'token')) {
        setAlert({
          message: 'Usuario creado exitosamente',
          msgType: 'success'
        })
        navigate('/usuarios')
      } else {
        setAlert({
          message: userData.message || 'Se produjo un error en el servidor',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al crear el usuario',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  return (
    <div className="add-user-container">
      <div className="container">
        <Row className="justify-content-center">
          <Col lg={8} md={10} sm={12}>
            {/* Header */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <FaUserPlus />
                </div>
                <div className="header-text">
                  <h1>Crear Nuevo Usuario</h1>
                  <p>Completa la información para registrar un nuevo usuario en el sistema</p>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <Card className="form-card">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    {/* Email Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaEnvelope className="field-icon" />
                          Correo Electrónico
                          <span className="required-field">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="usuario@ejemplo.com"
                          value={userEmail}
                          onChange={handleChangeEmail}
                          className={`form-control-custom ${errors.email ? 'is-invalid' : ''}`}
                        />
                        {errors.email && (
                          <div className="error-message">
                            <FaExclamationTriangle className="error-icon" />
                            {errors.email}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Role Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaShieldAlt className="field-icon" />
                          Rol del Usuario
                        </Form.Label>
                        <Form.Control
                          as="select"
                          className="form-control-custom"
                          value="ADMIN"
                          disabled
                        >
                          <option value="ADMIN">Administrador</option>
                        </Form.Control>
                        <Form.Text className="text-muted">
                          Los nuevos usuarios se crean como administradores por defecto
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    {/* First Name Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaUser className="field-icon" />
                          Nombre
                          <span className="required-field">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Nombre del usuario"
                          value={firstName}
                          onChange={handleChangeFirstName}
                          className={`form-control-custom ${errors.firstName ? 'is-invalid' : ''}`}
                        />
                        {errors.firstName && (
                          <div className="error-message">
                            <FaExclamationTriangle className="error-icon" />
                            {errors.firstName}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Last Name Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaUser className="field-icon" />
                          Apellidos
                          <span className="required-field">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Apellidos del usuario"
                          value={lastName}
                          onChange={handleChangeLastName}
                          className={`form-control-custom ${errors.lastName ? 'is-invalid' : ''}`}
                        />
                        {errors.lastName && (
                          <div className="error-message">
                            <FaExclamationTriangle className="error-icon" />
                            {errors.lastName}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Country Field */}
                    <Col lg={12} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaGlobe className="field-icon" />
                          País de Residencia
                          <span className="required-field">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="País donde reside el usuario"
                          value={country}
                          onChange={handleChangeCountry}
                          className={`form-control-custom ${errors.country ? 'is-invalid' : ''}`}
                        />
                        {errors.country && (
                          <div className="error-message">
                            <FaExclamationTriangle className="error-icon" />
                            {errors.country}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  <div className="form-actions">
                                         <Button
                       type="button"
                       variant="outline-secondary"
                       className="cancel-button"
                       onClick={() => navigate('/usuarios')}
                       disabled={isLoading}
                     >
                       Cancelar
                     </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="save-button"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creando...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Crear Usuario
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
